import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Tooltip,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../../Api/endpoints';
import { addAuthHeader } from '../../../../utils/authUtils';

/**
 * Document status constants
 */
const documentStatuses = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  EXPIRING_SOON: 'EXPIRING_SOON',
};

/**
 * Get status chip color
 */
const getStatusColor = (status) => {
  switch (status) {
    case documentStatuses.ACTIVE:
      return 'success';
    case documentStatuses.EXPIRED:
      return 'error';
    case documentStatuses.EXPIRING_SOON:
      return 'warning';
    default:
      return 'default';
  }
};

/**
 * Get status label
 */
const getStatusLabel = (status) => {
  switch (status) {
    case documentStatuses.ACTIVE:
      return 'Active';
    case documentStatuses.EXPIRED:
      return 'Expired';
    case documentStatuses.EXPIRING_SOON:
      return 'Expiring Soon';
    default:
      return status;
  }
};

/**
 * Get file icon based on type
 */
const getFileIcon = (fileType) => {
  if (fileType?.includes('pdf')) {
    return <PictureAsPdfIcon sx={{ fontSize: 48, color: '#f44336' }} />;
  }
  if (fileType?.includes('image')) {
    return <ImageIcon sx={{ fontSize: 48, color: '#4caf50' }} />;
  }
  return <DescriptionIcon sx={{ fontSize: 48, color: '#2196f3' }} />;
};

/**
 * Format date
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format file size
 */
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Format datetime
 */
const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Tab Panel Component
 */
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`document-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

/**
 * Document Detail Dialog
 * Shows full document details including:
 * - File preview area
 * - Version history from API group documents
 */
const DocumentDetailDialog = ({ open, onClose, document, onRefresh, initialTab = 0 }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewContentType, setPreviewContentType] = useState(null);

  useEffect(() => {
    if (open) {
      // If requesting history tab but no versions, fall back to details
      if (initialTab === 1 && versions.length <= 1) {
        setActiveTab(0);
      } else {
        setActiveTab(initialTab);
      }
    }
  }, [open, initialTab]);

  if (!document) return null;

  // Files in the current version group
  const currentGroupDocs = document.currentGroupDocs || [document];

  // Version history from all documents across all version groups for this type
  const versions = (document.allVersionDocs || [])
    .slice()
    .sort((a, b) => (b.version || 0) - (a.version || 0));

  const hasVersions = versions.length > 1;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const downloadFile = async (filePath, fileName) => {
    const downloadUrl = API_ENDPOINTS.FILES.DOWNLOAD(filePath);
    const response = await axios.get(downloadUrl, {
      headers: addAuthHeader(),
      responseType: 'blob'
    });
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = async () => {
    try {
      for (const file of currentGroupDocs) {
        if (file.filePath) {
          await downloadFile(file.filePath, file.fileName);
        }
      }
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleVersionDownload = async (versionDoc) => {
    if (!versionDoc.filePath) return;
    try {
      await downloadFile(versionDoc.filePath, versionDoc.fileName);
    } catch (err) {
      console.error('Version download failed:', err);
    }
  };

  const handleEdit = () => {
    console.log('Edit document:', document.id);
  };

  const handlePreviewFile = async (file) => {
    if (!file.filePath) return;
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewLoading(true);
    try {
      const previewEndpoint = API_ENDPOINTS.FILES.DOWNLOAD_BYTES(file.filePath);
      const response = await axios.get(previewEndpoint, {
        headers: addAuthHeader(),
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: file.contentType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewContentType(file.contentType);
    } catch (err) {
      console.error('Preview failed:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClose = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewContentType(null);
    setActiveTab(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6">{document.documentTypeName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {document.categoryName}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Status Alert */}
        {document.status === documentStatuses.EXPIRED && (
          <Alert severity="error" sx={{ mb: 2 }}>
            This document has expired. Please upload a renewed version.
          </Alert>
        )}
        {document.status === documentStatuses.EXPIRING_SOON && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This document expires in {document.daysUntilExpiry} days. Consider renewing soon.
          </Alert>
        )}

        {/* Status & Version */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip
            label={getStatusLabel(document.status)}
            size="small"
            color={getStatusColor(document.status)}
          />
          {document.version > 1 && (
            <Chip label={`Version ${document.version}`} size="small" variant="outlined" />
          )}
          {currentGroupDocs.length > 1 && (
            <Chip label={`${currentGroupDocs.length} files`} size="small" variant="outlined" />
          )}
        </Box>

        {/* File(s) in current group */}
        {currentGroupDocs.map((file, idx) => (
          <Paper key={file.id || idx} variant="outlined" sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            {getFileIcon(file.contentType)}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight="medium">
                {file.fileName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatFileSize(file.fileSize)} {file.uploadDate && `\u2022 Uploaded ${formatDateTime(file.uploadDate)}`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Preview">
                <IconButton onClick={() => handlePreviewFile(file)} disabled={previewLoading}>
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton onClick={() => handleVersionDownload(file)}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        ))}

        {/* Preview Area */}
        {previewUrl && (
          <Paper variant="outlined" sx={{ mb: 2, p: 1, textAlign: 'center' }}>
            {previewContentType?.includes('image') ? (
              <img
                src={previewUrl}
                alt="preview"
                style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain' }}
              />
            ) : (
              <object
                data={previewUrl}
                type={previewContentType || 'application/pdf'}
                width="100%"
                height="400"
              >
                <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                  Unable to preview this file type. Please download instead.
                </Typography>
              </object>
            )}
          </Paper>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Details" />
            {hasVersions && <Tab label={`History (${versions.length})`} />}
          </Tabs>
        </Box>

        {/* Details Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Document Information
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'medium', width: '40%' }}>Document Type</TableCell>
                    <TableCell>{document.documentTypeName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'medium' }}>Category</TableCell>
                    <TableCell>{document.categoryName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'medium' }}>Issuing Authority</TableCell>
                    <TableCell>{document.issuingAuthority || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'medium' }}>Document Number</TableCell>
                    <TableCell>{document.documentNumber || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'medium' }}>Issue Date</TableCell>
                    <TableCell>{formatDate(document.issueDate)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'medium' }}>Expiry Date</TableCell>
                    <TableCell>
                      {formatDate(document.expiryDate)}
                      {document.daysUntilExpiry !== null && document.daysUntilExpiry > 0 && (
                        <Typography
                          variant="caption"
                          color={document.daysUntilExpiry <= 30 ? 'warning.main' : 'text.secondary'}
                          sx={{ ml: 1 }}
                        >
                          ({document.daysUntilExpiry} days remaining)
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Upload Information
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'medium', width: '40%' }}>Uploaded By</TableCell>
                    <TableCell>{document.uploadedByName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'medium' }}>Uploaded At</TableCell>
                    <TableCell>{formatDateTime(document.uploadedAt)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'medium' }}>Version</TableCell>
                    <TableCell>{document.version}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'medium' }}>File Size</TableCell>
                    <TableCell>{document.fileSizeFormatted || '-'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>

            {document.notes && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body2">{document.notes}</Typography>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* History Tab */}
        {hasVersions && (
          <TabPanel value={activeTab} index={1}>
            <Table size="small">
              <TableBody>
                {versions.map((ver) => (
                  <TableRow key={ver.id}>
                    <TableCell sx={{ width: 120 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={`v${ver.version}`}
                          size="small"
                          color={ver.isCurrent ? 'primary' : 'default'}
                          variant={ver.isCurrent ? 'filled' : 'outlined'}
                        />
                        {ver.isPrimary && ver.isCurrent && (
                          <Chip label="Current" color="success" size="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{ver.fileName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{ver.uploadedByName || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDateTime(ver.uploadDate)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Download this version">
                        <IconButton size="small" onClick={() => handleVersionDownload(ver)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabPanel>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>Close</Button>
        <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
          Edit
        </Button>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentDetailDialog;
